import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Unlock, Loader2, Users, Check } from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Tela exclusiva do admin: afiliados do plano Iniciante (R$ 49,90) nao
 * escolhem sozinhos onde vender - o admin decide, um por um, quais 2
 * marketplaces cada um pode usar.
 */
const LiberarIniciantes = () => {
  const [afiliados, setAfiliados] = useState([]);
  const [marketplacesDisponiveis, setMarketplacesDisponiveis] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [selecoes, setSelecoes] = useState({});
  const [salvandoId, setSalvandoId] = useState(null);

  const carregar = async () => {
    setCarregando(true);
    try {
      const [respAfiliados, respMarketplaces] = await Promise.all([
        authService.fetchAuth(`${BACKEND_URL}/api/admin/afiliados-plano-iniciante`),
        authService.fetchAuth(`${BACKEND_URL}/api/oauth/marketplaces`),
      ]);
      if (respAfiliados.ok) setAfiliados(await respAfiliados.json());
      if (respMarketplaces.ok) setMarketplacesDisponiveis(await respMarketplaces.json());
    } catch (error) {
      console.error("Erro ao carregar:", error);
      toast.error("Não foi possível carregar os afiliados.");
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const alternarSelecao = (afiliadoId, marketplaceId) => {
    setSelecoes((prev) => {
      const atuais = prev[afiliadoId] || [];
      if (atuais.includes(marketplaceId)) {
        return { ...prev, [afiliadoId]: atuais.filter((m) => m !== marketplaceId) };
      }
      if (atuais.length >= 2) {
        toast.error("Máximo de 2 marketplaces no plano Iniciante.");
        return prev;
      }
      return { ...prev, [afiliadoId]: [...atuais, marketplaceId] };
    });
  };

  const salvar = async (afiliadoId) => {
    const escolhidos = selecoes[afiliadoId] || [];
    if (escolhidos.length === 0) {
      toast.error("Escolha pelo menos 1 marketplace.");
      return;
    }
    setSalvandoId(afiliadoId);
    try {
      const resp = await authService.fetchAuth(
        `${BACKEND_URL}/api/perfil/marketplaces-liberados-iniciante/${afiliadoId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ marketplaces: escolhidos }),
        }
      );
      if (!resp.ok) throw new Error("Erro ao liberar");
      toast.success("Marketplaces liberados para este afiliado!");
      await carregar();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvandoId(null);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Unlock className="w-7 h-7 text-orange-400" />
        <div>
          <h1 className="text-2xl font-bold text-white">Liberar Marketplaces — Plano Iniciante</h1>
          <p className="text-sm text-gray-400">Escolha até 2 marketplaces para cada afiliado do plano Iniciante (R$ 49,90)</p>
        </div>
      </div>

      {carregando ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Carregando...
        </div>
      ) : afiliados.length === 0 ? (
        <Card className="p-8 text-center text-gray-400 bg-gray-800/50 border-gray-700">
          <Users className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          Nenhum afiliado no plano Iniciante ainda.
        </Card>
      ) : (
        <div className="space-y-4">
          {afiliados.map((afiliado) => {
            const jaLiberados = afiliado.marketplaces_liberados_iniciante || [];
            const selecaoAtual = selecoes[afiliado.id] || jaLiberados;
            return (
              <Card key={afiliado.id} className="p-4 bg-gray-800/50 border-gray-700">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-white font-medium">{afiliado.nome}</p>
                    <p className="text-xs text-gray-400">{afiliado.email}</p>
                  </div>
                  {jaLiberados.length > 0 && (
                    <span className="text-[10px] bg-emerald-600 text-white px-2 py-0.5 rounded-full">
                      {jaLiberados.length} liberado(s)
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {marketplacesDisponiveis.map((mp) => {
                    const marcado = selecaoAtual.includes(mp.id);
                    return (
                      <button
                        key={mp.id}
                        onClick={() => alternarSelecao(afiliado.id, mp.id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                          marcado ? "border-orange-500 bg-orange-500/10 text-orange-400" : "border-gray-700 text-gray-400"
                        }`}
                      >
                        {marcado && <Check className="w-3 h-3 inline mr-1" />}
                        {mp.name}
                      </button>
                    );
                  })}
                </div>
                <Button
                  size="sm"
                  onClick={() => salvar(afiliado.id)}
                  disabled={salvandoId === afiliado.id}
                  className="bg-orange-600 hover:bg-orange-500"
                >
                  {salvandoId === afiliado.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  Salvar Liberação
                </Button>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LiberarIniciantes;
